import type { IconName } from "@/components/ui/Icon.vue";

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  admin?: boolean;
  /** for the organisation's admins and a workspace's admins (2026-10-27) */
  teamAdmin?: boolean;
  match?: RegExp;
  /** the family of pages it opens (its tabs, `FAMILIES`): lit while any of them is open */
  family?: string;
}
/** Whether to list `n` for this person; the server still decides what they may open. */
export const listed = (n: Pick<NavItem, "admin" | "teamAdmin">, admin: boolean, teamAdmin: boolean) =>
  (!n.admin || admin) && (!n.teamAdmin || admin || teamAdmin);

// The sidebar (2026-11-04, Codex Astra's navigation plan, docs/superpowers/plans/2026-11-04-navigation-astra.md; Amine:
// "I lose myself in all of those options and even my team told me its a little confusing"): what people come to do, ten
// destinations at most; every other page is a tab of its family's page (`FAMILIES`) and found by ⌘K (`destinations`).
// Home stays first (his decision); Guide and Feedback are pinned at the bottom (`utilities`).
export const navigation: { label: string; items: NavItem[] }[] = [
  { label: "Work", items: [
    { to: "/", label: "Home", icon: "grid", family: "home" },
    { to: "/ask", label: "Ask Majlis", icon: "message" },
    { to: "/agents", label: "Agents", icon: "sparkle", family: "agents" },
    { to: "/tickets", label: "Tickets", icon: "branch", family: "tickets" },
    { to: "/decisions", label: "Decisions", icon: "check" },
  ] },
  { label: "Resources", items: [
    { to: "/projects", label: "Jira projects", icon: "layers", family: "projects" },
    { to: "/connectors", label: "Connectors", icon: "link" },   // workspaces bring their own connections (H-153)
    { to: "/notes", label: "Knowledge", icon: "file", family: "knowledge" },
    { to: "/flows", label: "Workflows", icon: "play", family: "workflows" },
  ] },
  { label: "", items: [
    { to: "/organisation", label: "Organisation", icon: "target", admin: true, family: "organisation" },
  ] },
];
/** Pinned at the sidebar's foot, beside Activity. */
export const utilities: NavItem[] = [
  { to: "/guide", label: "Guide", icon: "book", family: "guide" },
  { to: "/feedback", label: "Feedback", icon: "message", family: "feedback" },
];

/** A page of a family: a tab on its family's pages. `{ws}` is the person's current workspace (no workspace: no tab). */
export interface Tab {
  to: string;
  label: string;
  admin?: boolean;
  teamAdmin?: boolean;
  /** the paths it holds besides its own (a ticket's page is Tickets') */
  match?: RegExp;
}
export const FAMILIES: Record<string, { title: string; tabs: Tab[] }> = {
  home: { title: "Home", tabs: [{ to: "/", label: "Mission Control" }, { to: "/brief", label: "Brief" }] },
  agents: { title: "Agents", tabs: [
    { to: "/agents", label: "Agents", match: /^\/agents(\/|$)/ },
    { to: "/bindings", label: "Tool access" },
    { to: "/quality", label: "Missed answers", teamAdmin: true },   // Amine's decision: under Agents
  ] },
  tickets: { title: "Tickets", tabs: [
    { to: "/tickets", label: "Tickets", match: /^\/(tickets|runs)(\/|$)/ },
    { to: "/compare", label: "Compare" },
  ] },
  projects: { title: "Jira projects", tabs: [
    { to: "/projects", label: "Projects", match: /^\/projects(\/|$)/ },
    { to: "/onboarding", label: "Connect a project", teamAdmin: true, match: /^\/onboarding(\/|$)/ },
    { to: "/readiness", label: "Repo readiness" },
    { to: "/map", label: "Code map" },   // Amine's decision: under Jira projects
    { to: "/lineage", label: "Everything connected" },
    { to: "/mappings", label: "Repository links", admin: true },
    { to: "/labs", label: "Code map labs", admin: true, match: /^\/labs(\/|$)/ },
  ] },
  knowledge: { title: "Knowledge", tabs: [
    { to: "/notes", label: "Notes" },
    { to: "/skills", label: "Skills" },
    { to: "/workspaces/{ws}/learning", label: "Lessons", match: /^\/workspaces\/[^/]+\/learning$/ },
    { to: "/workspaces/{ws}/biographie", label: "Biographie", match: /^\/workspaces\/[^/]+\/biographie$/ },
  ] },
  workflows: { title: "Workflows", tabs: [
    { to: "/flows", label: "Workflows", match: /^\/(flows|run)(\/|$)/ },
    { to: "/configs", label: "Saved settings" },
    { to: "/schedules", label: "Schedules" },   // an agent's own schedules stay on its page
  ] },
  organisation: { title: "Organisation", tabs: [
    { to: "/organisation", label: "Overview", admin: true },
    { to: "/workspaces", label: "Workspaces", match: /^\/workspaces(?!\/[^/]+\/(learning|biographie)$)(\/|$)/ },
    { to: "/people", label: "People", admin: true },
    { to: "/leaders", label: "Leaders", admin: true },
    { to: "/budgets", label: "Budgets", admin: true },
    { to: "/biographie-channels", label: "Biographie channels", admin: true },
  ] },
  feedback: { title: "Feedback", tabs: [
    { to: "/feedback", label: "My feedback", match: /^\/feedback(\/(?!inbox|shared)|$)/ },
    { to: "/feedback/shared", label: "Shared feedback", teamAdmin: true, match: /^\/feedback\/shared(\/|$)/ },
    { to: "/feedback/inbox", label: "Feedback inbox", admin: true },
  ] },
  guide: { title: "Guide", tabs: [
    { to: "/guide", label: "Walkthroughs", match: /^\/guide(\/|$)/ },
    { to: "/architecture", label: "How Majlis works", match: /^\/architecture(\/|$)/ },
  ] },
};

/** A tab's path for this person: `{ws}` their current workspace; null when it needs one they don't have. */
export function tabPath(t: Tab, workspace: string | null | undefined): string | null {
  if (!t.to.includes("{ws}")) return t.to;
  return workspace ? t.to.replace("{ws}", encodeURIComponent(workspace)) : null;
}
const holds = (t: Tab, path: string) => (t.match ? t.match.test(path) : path === t.to);
/** The family whose pages hold `path`, and its tab that does (null: a page of no family). */
export function familyOf(path: string): { family: string; tab: Tab } | null {
  for (const [family, f] of Object.entries(FAMILIES)) {
    const tab = f.tabs.find((t) => holds(t, path));
    if (tab) return { family, tab };
  }
  return null;
}
/** The tabs this person sees on a family's page: those they may open (`listed`), with their paths. */
export function familyTabs(family: string, admin: boolean, teamAdmin: boolean, workspace: string | null | undefined): (Tab & { path: string })[] {
  return (FAMILIES[family]?.tabs ?? []).filter((t) => listed(t, admin, teamAdmin))
    .flatMap((t) => { const path = tabPath(t, workspace); return path ? [{ ...t, path }] : []; });
}
/** Whether a sidebar item is lit for `path`: its family's page is open, or its own. */
export function isOnPath(n: NavItem, path: string): boolean {
  if (n.family) return familyOf(path)?.family === n.family;
  if (n.match) return n.match.test(path);
  return path === n.to || (n.to !== "/" && path.startsWith(`${n.to}/`));
}

/** A page ⌘K finds: the sidebar's, and those that live inside another page, with the words people look for it by (its
 * old names too) and the page that holds it. */
export interface Destination extends NavItem {
  parent?: string;
  aliases?: string[];
}

// the words people search by, old names included (2026-11-04, Codex Astra's navigation plan, slice 2: the sidebar gets
// shorter, ⌘K keeps finding everything; docs/superpowers/plans/2026-11-04-navigation-astra.md)
const ALIASES: Record<string, string[]> = {
  "/": ["home", "mission control", "dashboard"],
  "/ask": ["chat", "question", "assistant"],
  "/tickets": ["jira tickets", "attempts", "runs"],
  "/guide": ["help", "how to", "walkthrough"],
  "/connectors": ["connectors", "integrations", "mcp", "datadog", "cloudwatch", "database"],
  "/agents": ["bots", "assistants", "@handle"],
  "/workspaces": ["teams", "members"],
  "/leaders": ["invite a leader", "onboarding"],
  "/projects": ["jira boards", "boards"],
  "/skills": ["knowledge", "instructions"],
  "/notes": ["notes", "team notes", "context", "lessons", "biographie"],
  "/onboarding": ["connect a repository", "github", "repository", "install the app"],
  "/decisions": ["approvals", "approve", "proposals", "levels"],
  "/budgets": ["spend", "cost", "limits"],
  "/biographie-channels": ["biographie channels", "slack channels"],
  "/bindings": ["bindings", "permissions", "tools"],
  "/readiness": ["repo readiness", "steering", "ci"],
  "/feedback/inbox": ["reports inbox", "triage"],
  "/quality": ["review answers", "failed answers", "quality"],
  "/feedback/shared": ["shared with my workspaces", "excerpts"],
  "/map": ["code graph"],
  "/labs": ["experiments", "radar", "atlas", "thread"],
  "/brief": ["brief", "report", "export"],
  "/architecture": ["architecture", "how it works", "layers"],
  "/compare": ["compare", "a/b"],
  "/flows": ["all flows", "windmill", "run a flow"],
  "/configs": ["configurations", "saved parameters"],
  "/schedules": ["workflow schedules", "cron"],
  "/mappings": ["mappings", "project repos"],
  "/lineage": ["everything connected", "graph"],
  "/feedback": ["my reports", "my feedback", "send feedback"],
  "/organisation": ["admin", "organisation settings"],
  "/people": ["members", "offboard", "users"],
};
const ICON: Record<string, IconName> = { home: "grid", agents: "sparkle", tickets: "branch", projects: "layers", knowledge: "file",
                                       workflows: "play", organisation: "target", feedback: "message", guide: "book" };
/** Every page ⌘K finds, whoever may see it (`listed`): the sidebar's, every family's tabs beside the page that holds them,
 * then pages that live inside others. A tab needing a workspace (Lessons, Biographie) is found from its workspace's page. */
export const destinations: Destination[] = [
  ...[...navigation.flatMap((g) => g.items), ...utilities].map((n) => ({ ...n, aliases: ALIASES[n.to] ?? [] })),
  ...Object.entries(FAMILIES).flatMap(([family, f]) => f.tabs
    .filter((t) => !t.to.includes("{ws}") && ![...navigation.flatMap((g) => g.items), ...utilities].some((n) => n.to === t.to))
    .map((t) => ({ to: t.to, label: t.label, icon: ICON[family] ?? "file", admin: t.admin, teamAdmin: t.teamAdmin, parent: f.title,
                   aliases: ALIASES[t.to] ?? [] }))),
  { to: "/connectors/addresses", label: "Approved addresses", icon: "link", parent: "Connectors", aliases: ["endpoints", "allowed addresses"] },
  { to: "/connectors/endpoint-rule", label: "Where connectors may reach", icon: "shield", admin: true, parent: "Connectors", aliases: ["endpoint rule"] },
  { to: "/connectors/snowflake", label: "Snowflake", icon: "layers", admin: true, parent: "Connectors", aliases: ["warehouse"] },
  { to: "/connectors/hosted-tools", label: "Hosted tools", icon: "settings", admin: true, parent: "Connectors", aliases: ["tool code"] },
  { to: "/architecture/aws", label: "Majlis on AWS", icon: "layers", parent: "How Majlis works", aliases: ["aws architecture", "diagrams", "infrastructure"] },
  { to: "/settings", label: "Settings", icon: "settings", aliases: ["profile", "preferences", "link slack", "link github", "notifications", "memory"] },
];

/** The destinations matching what someone typed: its label, an old name or the page that holds it. */
export function findDestinations(all: Destination[], query: string): Destination[] {
  const q = query.trim().toLowerCase();
  if (!q) return all;
  return all.filter((d) => [d.label, d.parent ?? "", ...(d.aliases ?? [])].some((w) => w.toLowerCase().includes(q)));
}
